import { AlertCircle, LoaderCircle, RefreshCw, UserRound } from "lucide-react";

import { Drawer } from "../../../../components/ui/Drawer";
import { CustomerDetailsController } from "../../../../services/controllers/customer";
import { CUSTOMER_DETAILS_DRAWER_NAME } from "../../../../services/controllers/customer/CustomerDetailsController";
import { useCustomerDetails } from "../../../../store/customer/useCustomerDetails";
import { CustomerDetailsSections } from "./CustomerDetailsSections";
import { CustomerDetailsSummary } from "./CustomerDetailsSummary";
import { formatDate } from "./helpers";

function CustomerDetailsSkeleton() {
  return (
    <div aria-label="Carregando detalhes do cliente" className="w-full animate-pulse space-y-5">
      <div className="h-40 rounded-lg bg-gray-100" />
      {[1, 2, 3, 4, 5].map((item) => (
        <div className="h-14 rounded-lg bg-gray-100" key={item} />
      ))}
    </div>
  );
}

function CustomerDetailsError({ message, onRetry }) {
  return (
    <div className="flex w-full flex-col items-center justify-center gap-4 rounded-lg border border-error bg-background p-8 text-center">
      <AlertCircle aria-hidden="true" className="text-error" size={32} />
      <div>
        <h2 className="font-semibold text-gray-800">Não foi possível carregar o cliente</h2>
        <p className="mt-1 text-sm text-gray-600">{message}</p>
      </div>
      <button
        className="inline-flex items-center gap-2 rounded-md bg-scale-500 px-4 py-2 text-sm font-semibold text-background focus:outline-none focus:ring-2 focus:ring-scale-300"
        onClick={onRetry}
        type="button"
      >
        <RefreshCw aria-hidden="true" size={16} />
        Tentar novamente
      </button>
    </div>
  );
}

export function CustomerDetailsDrawer() {
  const controller = CustomerDetailsController();
  const activeCustomerId = useCustomerDetails((state) => state.data.activeCustomerId);
  const cachedCustomer = useCustomerDetails((state) =>
    activeCustomerId ? state.data.cache[activeCustomerId] : null
  );
  const loading = useCustomerDetails((state) => state.data.loading);
  const refreshing = useCustomerDetails((state) => state.data.refreshing);
  const error = useCustomerDetails((state) => state.data.error);
  const details = cachedCustomer?.details;

  return (
    <Drawer.Root
      direction="left"
      name={CUSTOMER_DETAILS_DRAWER_NAME}
      style={{ minWidth: "560px" }}
    >
      <Drawer.Controls controls={{ close: controller.close }} />
      <Drawer.Header>
        <div className="flex w-full items-center justify-between gap-4">
          <span className="flex items-center gap-2">
            <UserRound aria-hidden="true" size={20} />
            Detalhes do cliente
          </span>
          {refreshing && (
            <span className="flex items-center gap-2 text-xs" role="status">
              <LoaderCircle aria-hidden="true" className="animate-spin" size={15} />
              Atualizando
            </span>
          )}
        </div>
      </Drawer.Header>

      <Drawer.Content style={{ flexDirection: "column", flexWrap: "nowrap" }}>
        {loading && !details && <CustomerDetailsSkeleton />}

        {!details && error && (
          <CustomerDetailsError message={error} onRetry={controller.retry} />
        )}

        {details && (
          <div className="w-full space-y-5">
            {error && (
              <div className="flex items-center justify-between gap-4 rounded-md border border-warning bg-background px-4 py-3 text-sm text-gray-700">
                <span>Os dados salvos foram mantidos, mas não foi possível atualizá-los.</span>
                <button
                  className="shrink-0 font-semibold text-scale-700 underline"
                  onClick={controller.retry}
                  type="button"
                >
                  Tentar novamente
                </button>
              </div>
            )}
            <CustomerDetailsSummary
              customer={details.customer}
              onNavigate={controller.close}
            />
            <CustomerDetailsSections details={details} />
          </div>
        )}
      </Drawer.Content>

      <Drawer.Footer>
        {cachedCustomer?.updatedAt
          ? `Última atualização: ${formatDate(cachedCustomer.updatedAt, true)}`
          : "Os dados serão atualizados ao abrir o cliente."}
      </Drawer.Footer>
    </Drawer.Root>
  );
}
